import type { AnchorHTMLAttributes, ReactNode } from 'react';

import { buildFooterReferralUrl } from '../utils/footerReferralUrl';

export type FooterReferralPublication = {
  name: string;
  logo?: { url?: string | null } | null;
  footer_referral_utm_source?: string | null;
};

type SharedProps = {
  utmMedium?: string;
  utmCampaign?: string;
  utmSource?: string | null;
  children: ReactNode;
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'target' | 'rel'>;

type WithPublication = SharedProps & {
  publication: FooterReferralPublication;
  publicationName?: never;
  publicationLogoUrl?: never;
};

type WithFields = SharedProps & {
  publication?: never;
  publicationName: string;
  publicationLogoUrl?: string | null;
};

export type FooterReferralLinkProps = WithPublication | WithFields;

const resolveParams = (props: FooterReferralLinkProps) => {
  if ('publication' in props && props.publication) {
    return {
      publicationName: props.publication.name,
      publicationLogoUrl: props.publication.logo?.url,
      utmSource: props.utmSource ?? props.publication.footer_referral_utm_source,
      utmMedium: props.utmMedium,
      utmCampaign: props.utmCampaign,
    };
  }

  return {
    publicationName: props.publicationName,
    publicationLogoUrl: props.publicationLogoUrl,
    utmSource: props.utmSource,
    utmMedium: props.utmMedium,
    utmCampaign: props.utmCampaign,
  };
};

const splitAnchorProps = (props: FooterReferralLinkProps) => {
  if ('publication' in props && props.publication) {
    const {
      publication: _publication,
      utmMedium: _utmMedium,
      utmCampaign: _utmCampaign,
      utmSource: _utmSource,
      children,
      ...anchorProps
    } = props;

    return { children, anchorProps };
  }

  const {
    publicationName: _publicationName,
    publicationLogoUrl: _publicationLogoUrl,
    utmMedium: _utmMedium,
    utmCampaign: _utmCampaign,
    utmSource: _utmSource,
    children,
    ...anchorProps
  } = props;

  return { children, anchorProps };
};

export const FooterReferralLink = (props: FooterReferralLinkProps) => {
  const href = buildFooterReferralUrl(resolveParams(props));
  const { children, anchorProps } = splitAnchorProps(props);

  return (
    <a {...anchorProps} href={href} target="_blank" rel="noreferrer">
      {children}
    </a>
  );
};
